import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@elbklar/ui';

// Layout - always loaded
import { PMLayout } from './components/layout/PMLayout';

// Auth - always loaded (needed for route protection)
import { useAuth, LoginPage, AuthCallbackPage } from './features/auth';

// Die gemeinsame Ladeansicht der Projektseiten, bewusst NICHT nachgeladen:
// Sie ist der Fallback, während das Bündel einer Projektseite kommt.
import { ProjektLaden } from './features/projects/components/ProjektLaden';

// Lazy-loaded feature pages for code-splitting
const MeineArbeitPage = lazy(() =>
  import('./features/meine-arbeit').then((m) => ({ default: m.MeineArbeitPage }))
);
const ProjectDetailPage = lazy(() =>
  import('./features/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage }))
);
const WebsiteProjectsPage = lazy(() =>
  import('./features/website/WebsiteProjectsPage').then((m) => ({ default: m.WebsiteProjectsPage }))
);

// Angebot workflow
const VorplanungPage = lazy(() =>
  import('./features/angebot').then((m) => ({ default: m.VorplanungPage }))
);
const BesichtigungPage = lazy(() =>
  import('./features/angebot').then((m) => ({ default: m.BesichtigungPage }))
);
const KalkulationPage = lazy(() =>
  import('./features/angebot').then((m) => ({ default: m.KalkulationPage }))
);
const AngebotPage = lazy(() =>
  import('./features/angebot').then((m) => ({ default: m.AngebotPage }))
);

// Loading fallback for lazy-loaded components
function PageLoader() {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Spinner size={24} />
        <p className="t-small text-muted-foreground">Seite wird geladen …</p>
      </div>
    </div>
  );
}

/**
 * Fallback der Projektseiten. Dieselbe Ansicht, die die Seite selbst zeigt,
 * während sie ihre Daten holt — vorher sah man beim Reiterwechsel zwei
 * verschiedene Dreher hintereinander an zwei verschiedenen Stellen: erst
 * diesen Fallback (mittig, „Seite wird geladen …“), dann den der Seite
 * (weiter oben). Dazwischen wusste man nicht, wo man ist.
 */
function ProjektLoader() {
  return <ProjektLaden laedt />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Protected PM routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <PMLayout />
          </ProtectedRoute>
        }
      >
        {/* Meine Arbeit (Dashboard) - Workflow-basiert */}
        <Route
          path="/"
          element={
            <Suspense fallback={<PageLoader />}>
              <MeineArbeitPage />
            </Suspense>
          }
        />

        {/* /dashboard ist das Post-Login-Redirect-Ziel (loginWithGoogle/AuthCallback);
            PMs Dashboard liegt auf "/" -> umleiten, sonst weiße Seite (kein Route-Match). */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />

        {/* Projects - nur Detail-Ansicht, kein manuelles Erstellen/Bearbeiten */}
        <Route
          path="/projects/:id"
          element={
            <Suspense fallback={<ProjektLoader />}>
              <ProjectDetailPage />
            </Suspense>
          }
        />

        {/* Angebotserstellung Workflow */}
        <Route
          path="/projects/:id/vorplanung"
          element={
            <Suspense fallback={<ProjektLoader />}>
              <VorplanungPage />
            </Suspense>
          }
        />
        <Route
          path="/projects/:id/besichtigung"
          element={
            <Suspense fallback={<ProjektLoader />}>
              <BesichtigungPage />
            </Suspense>
          }
        />
        <Route
          path="/projects/:id/kalkulation"
          element={
            <Suspense fallback={<ProjektLoader />}>
              <KalkulationPage />
            </Suspense>
          }
        />
        <Route
          path="/projects/:id/angebot"
          element={
            <Suspense fallback={<ProjektLoader />}>
              <AngebotPage />
            </Suspense>
          }
        />

        {/* Website Management */}
        <Route
          path="/website"
          element={
            <Suspense fallback={<PageLoader />}>
              <WebsiteProjectsPage />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
